import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  OutlinedInput,
} from "@mui/material";
import { observer } from "mobx-react";
import dayjs, { Dayjs } from "dayjs";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import DatePicker2 from "../../../../../views/components/common/textFields/DatePicker2";
import {
  CreateSwayyCouponPayload,
  SwayyCouponChannel,
  SwayyCouponDiscountType,
  UpdateSwayyCouponPayload,
  toSwayyCouponDateIso,
} from "../../../../../models/SwayyCoupon/SwayyCoupon";
import {
  SWAYY_COUPON_ACTIVE_OPTIONS,
  SWAYY_COUPON_CHANNEL_OPTIONS,
  SWAYY_COUPON_DISCOUNT_TYPE_OPTIONS,
} from "../../../../../models/SwayyCoupon/SwayyCouponFilters";
import {
  SwayyCouponFormValues,
  swayyCouponStore,
} from "../../../stores/SwayyCouponStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import { SWAYY_FILTER_DROPDOWN_DEFAULTS } from "../filters/SwayySearchFiltersPanel";

const YES_NO_OPTIONS = [
  { id: "false", value: "No" },
  { id: "true", value: "Yes" },
];

const inputSx = {
  borderRadius: swayyRadii.md,
  fontFamily: swayyFont,
  fontSize: 13,
  height: 40,
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-input": {
    py: 0.75,
    height: 40,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: swayyColors.border },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
} as const;

const FieldLabel = ({ label }: { label: string }) => (
  <CustomTypography
    variant="body3"
    color={swayyColors.textSecondary}
    sx={{ fontFamily: swayyFont, fontSize: 12, fontWeight: 600, mb: 0.75 }}
  >
    {label}
  </CustomTypography>
);

type CouponFormValidation =
  | { ok: false; error: string }
  | { ok: true; parsedDiscount: number };

const minSelectableDate = () => dayjs().startOf("day");

const isPastDate = (value: Dayjs | null): boolean =>
  value != null && value.isBefore(minSelectableDate(), "day");

const validateCouponForm = (
  values: SwayyCouponFormValues,
  options?: { skipScheduleValidation?: boolean },
): CouponFormValidation => {
  const trimmedCode = values.code.trim().toUpperCase();
  if (!trimmedCode) {
    return { ok: false, error: "Coupon code is required." };
  }
  if (!/^[A-Z0-9_-]+$/.test(trimmedCode)) {
    return {
      ok: false,
      error: "Coupon code can only contain letters, numbers, hyphens (-), and underscores (_).",
    };
  }

  const parsedDiscount = Number(values.discountValue);
  if (
    values.discountType !== "free_shipping" &&
    (!Number.isFinite(parsedDiscount) || parsedDiscount <= 0)
  ) {
    return { ok: false, error: "Enter a valid discount value." };
  }
  if (values.discountType === "percent" && parsedDiscount > 100) {
    return { ok: false, error: "Percent discount must be between 0 and 100." };
  }
  if (!options?.skipScheduleValidation) {
    if (isPastDate(values.startsAt)) {
      return {
        ok: false,
        error: "Start date should be today or a future date.",
      };
    }
    if (isPastDate(values.endsAt)) {
      return {
        ok: false,
        error: "End date should be today or a future date.",
      };
    }
    if (
      values.startsAt &&
      values.endsAt &&
      values.endsAt.startOf("day").isBefore(values.startsAt.startOf("day"))
    ) {
      return { ok: false, error: "Start date should be on or before end date." };
    }
  }
  if (
    values.usageLimit.trim() &&
    (!Number.isInteger(Number(values.usageLimit)) || Number(values.usageLimit) <= 0)
  ) {
    return { ok: false, error: "Usage limit must be a positive whole number." };
  }
  if (
    !Number.isInteger(Number(values.usagePerUser)) ||
    Number(values.usagePerUser) <= 0
  ) {
    return { ok: false, error: "Usage per user must be a positive whole number." };
  }

  return { ok: true, parsedDiscount };
};

const toCreatePayload = (
  values: SwayyCouponFormValues,
  parsedDiscount: number,
): CreateSwayyCouponPayload => ({
  code: values.code.replace(/[^A-Za-z0-9_-]/g, "").toUpperCase(),
  description: values.description.trim() || null,
  discount_type: values.discountType,
  discount_value: values.discountType === "free_shipping" ? 0 : parsedDiscount,
  max_discount:
    values.discountType === "percent" && values.maxDiscount.trim()
      ? Number(values.maxDiscount)
      : null,
  min_order_value: Number(values.minOrderValue) || 0,
  channel: values.channel,
  scope: "all",
  usage_limit: values.usageLimit.trim() ? Number(values.usageLimit) : null,
  usage_per_user: Number(values.usagePerUser) || 1,
  first_order_only: values.firstOrderOnly,
  starts_at: values.startsAt ? toSwayyCouponDateIso(values.startsAt) : null,
  ends_at: values.endsAt ? toSwayyCouponDateIso(values.endsAt) : null,
  is_active: values.isActive,
});

const toUpdatePayload = (
  values: SwayyCouponFormValues,
  parsedDiscount: number,
  hasRedemptions: boolean,
): UpdateSwayyCouponPayload => {
  if (hasRedemptions) {
    return {
      description: values.description.trim() || null,
      is_expired: values.isExpired,
    };
  }

  return {
    description: values.description.trim() || null,
    usage_limit: values.usageLimit.trim() ? Number(values.usageLimit) : null,
    starts_at: values.startsAt ? toSwayyCouponDateIso(values.startsAt) : null,
    ends_at: values.endsAt ? toSwayyCouponDateIso(values.endsAt) : null,
    is_active: values.isActive,
    is_expired: values.isExpired,
    discount_type: values.discountType,
    discount_value:
      values.discountType === "free_shipping" ? 0 : parsedDiscount,
    max_discount:
      values.discountType === "percent" && values.maxDiscount.trim()
        ? Number(values.maxDiscount)
        : null,
    min_order_value: Number(values.minOrderValue) || 0,
    channel: values.channel,
    usage_per_user: Number(values.usagePerUser) || 1,
    first_order_only: values.firstOrderOnly,
  };
};

interface SwayyCreateCouponDialogProps {
  onCreated?: () => void;
  onUpdated?: () => void;
  onError: (message: string) => void;
  canCreate?: boolean;
  canEdit?: boolean;
}

const SwayyCreateCouponDialog = ({
  onCreated,
  onUpdated,
  onError,
  canCreate = false,
  canEdit = false,
}: SwayyCreateCouponDialogProps) => {
  const store = swayyCouponStore;
  const editingCoupon = store.editingCoupon;
  const isEdit = editingCoupon != null;
  const hasRedemptions = (editingCoupon?.times_redeemed ?? 0) > 0;
  const lockAfterRedemption = isEdit && hasRedemptions;
  const isOpen =
    (canCreate && store.createOpen) || (canEdit && isEdit);
  const values = store.form;
  const formError = store.formError;

  const handleClose = () => {
    store.closeCreate();
  };

  const handleSubmit = async () => {
    try {
      store.setFormError("");
      const validated = validateCouponForm(values, {
        skipScheduleValidation: hasRedemptions,
      });
      if (!validated.ok) {
        store.setFormError(validated.error);
        return;
      }

      if (isEdit && editingCoupon) {
        if (!canEdit) {
          const message = "You do not have permission to edit coupons.";
          store.setFormError(message);
          onError(message);
          return;
        }
        await store.updateCoupon(
          editingCoupon.id,
          toUpdatePayload(values, validated.parsedDiscount, hasRedemptions),
        );
        await store.getCoupons();
        handleClose();
        onUpdated?.();
        return;
      }

      if (!canCreate) {
        const message = "You do not have permission to create coupons.";
        store.setFormError(message);
        onError(message);
        return;
      }

      await store.createCoupon(toCreatePayload(values, validated.parsedDiscount));
      onCreated?.();
    } catch (error: any) {
      const message =
        error?.message ||
        (isEdit ? "Failed to update coupon." : "Failed to create coupon.");
      store.setFormError(message);
      onError(message);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      sx={{ zIndex: 4200 }}
      PaperProps={{
        sx: {
          borderRadius: swayyRadii.lg,
          border: `1px solid ${swayyColors.border}`,
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: swayyFont, fontWeight: 700 }}>
        {isEdit ? "Edit coupon" : "Create coupon"}
      </DialogTitle>
      <DialogContent>
        {lockAfterRedemption ? (
          <CustomTypography
            variant="body3"
            color={swayyColors.textSecondary}
            sx={{ fontFamily: swayyFont, fontSize: 12, mb: 1.5 }}
          >
            This coupon has redemptions. Only description and expired can be
            changed.
          </CustomTypography>
        ) : null}
        <Grid2 container spacing={2} sx={{ mt: 0.5 }}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="Code" />
            <OutlinedInput
              fullWidth
              disabled={isEdit}
              value={values.code}
              onChange={(event) =>
                store.patchForm({
                  code: event.target.value
                    .replace(/[^A-Za-z0-9_-]/g, "")
                    .toUpperCase(),
                })
              }
              onKeyDown={(event) => {
                if (event.key === " ") {
                  event.preventDefault();
                }
              }}
              inputProps={{
                autoComplete: "off",
                spellCheck: false,
              }}
              placeholder="WELCOME10"
              sx={inputSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="Discount type" />
            <AppSearchDropdownMulti
              title="Select type"
              isMultiSelect={false}
              disabled={lockAfterRedemption}
              list={SWAYY_COUPON_DISCOUNT_TYPE_OPTIONS}
              value={SWAYY_COUPON_DISCOUNT_TYPE_OPTIONS.filter(
                (option) => option.id === values.discountType,
              )}
              {...SWAYY_FILTER_DROPDOWN_DEFAULTS}
              handleSelect={(items) =>
                store.patchForm({
                  discountType:
                    (items[0]?.id as SwayyCouponDiscountType) || "percent",
                })
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <FieldLabel label="Description" />
            <OutlinedInput
              fullWidth
              value={values.description}
              onChange={(event) =>
                store.patchForm({ description: event.target.value })
              }
              placeholder="Shown to customers"
              sx={inputSx}
            />
          </Grid2>
          {values.discountType !== "free_shipping" ? (
            <Grid2 size={{ xs: 12, md: 6 }}>
              <FieldLabel
                label={
                  values.discountType === "percent" ? "Percent" : "Amount (₹)"
                }
              />
              <OutlinedInput
                fullWidth
                disabled={lockAfterRedemption}
                value={values.discountValue}
                onChange={(event) =>
                  store.patchForm({ discountValue: event.target.value })
                }
                sx={inputSx}
              />
            </Grid2>
          ) : null}
          {values.discountType === "percent" ? (
            <Grid2 size={{ xs: 12, md: 6 }}>
              <FieldLabel label="Max discount (₹)" />
              <OutlinedInput
                fullWidth
                disabled={lockAfterRedemption}
                value={values.maxDiscount}
                onChange={(event) =>
                  store.patchForm({ maxDiscount: event.target.value })
                }
                placeholder="Optional"
                sx={inputSx}
              />
            </Grid2>
          ) : null}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="Min order value (₹)" />
            <OutlinedInput
              fullWidth
              disabled={lockAfterRedemption}
              value={values.minOrderValue}
              onChange={(event) =>
                store.patchForm({ minOrderValue: event.target.value })
              }
              sx={inputSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="Channel" />
            <AppSearchDropdownMulti
              title="Select channel"
              isMultiSelect={false}
              disabled={lockAfterRedemption}
              list={SWAYY_COUPON_CHANNEL_OPTIONS}
              value={SWAYY_COUPON_CHANNEL_OPTIONS.filter(
                (option) => option.id === values.channel,
              )}
              {...SWAYY_FILTER_DROPDOWN_DEFAULTS}
              handleSelect={(items) =>
                store.patchForm({
                  channel: (items[0]?.id as SwayyCouponChannel) || "b2c",
                })
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="Usage limit" />
            <OutlinedInput
              fullWidth
              disabled={lockAfterRedemption}
              value={values.usageLimit}
              onChange={(event) =>
                store.patchForm({ usageLimit: event.target.value })
              }
              placeholder="Unlimited"
              sx={inputSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="Usage per user" />
            <OutlinedInput
              fullWidth
              disabled={lockAfterRedemption}
              value={values.usagePerUser}
              onChange={(event) =>
                store.patchForm({ usagePerUser: event.target.value })
              }
              sx={inputSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="Status" />
            <AppSearchDropdownMulti
              title="Select status"
              isMultiSelect={false}
              disabled={lockAfterRedemption}
              list={SWAYY_COUPON_ACTIVE_OPTIONS}
              value={SWAYY_COUPON_ACTIVE_OPTIONS.filter(
                (option) => option.id === String(values.isActive),
              )}
              {...SWAYY_FILTER_DROPDOWN_DEFAULTS}
              handleSelect={(items) =>
                store.patchForm({ isActive: items[0]?.id !== "false" })
              }
            />
          </Grid2>
          {isEdit ? (
            <Grid2 size={{ xs: 12, md: 6 }}>
              <FieldLabel label="Expired" />
              <AppSearchDropdownMulti
                title="Expired"
                isMultiSelect={false}
                list={YES_NO_OPTIONS}
                value={[
                  {
                    id: String(values.isExpired),
                    value: values.isExpired ? "Yes" : "No",
                  },
                ]}
                {...SWAYY_FILTER_DROPDOWN_DEFAULTS}
                handleSelect={(items) =>
                  store.patchForm({ isExpired: items[0]?.id === "true" })
                }
              />
            </Grid2>
          ) : null}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="First order only" />
            <AppSearchDropdownMulti
              title="First order"
              isMultiSelect={false}
              disabled={lockAfterRedemption}
              list={YES_NO_OPTIONS}
              value={[
                {
                  id: String(values.firstOrderOnly),
                  value: values.firstOrderOnly ? "Yes" : "No",
                },
              ]}
              {...SWAYY_FILTER_DROPDOWN_DEFAULTS}
              handleSelect={(items) =>
                store.patchForm({ firstOrderOnly: items[0]?.id === "true" })
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="Start date" />
            <DatePicker2
              type="date"
              label=""
              value={values.startsAt}
              onChange={(startsAt) => store.patchForm({ startsAt })}
              disabled={lockAfterRedemption}
              minDate={minSelectableDate()}
              maxDate={values.endsAt || undefined}
              error={isPastDate(values.startsAt)}
              zIndex={5000}
              borderColor={swayyColors.border}
              borderRadius={swayyRadii.md}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FieldLabel label="End date" />
            <DatePicker2
              type="date"
              label=""
              value={values.endsAt}
              onChange={(endsAt) => store.patchForm({ endsAt })}
              disabled={lockAfterRedemption}
              minDate={
                values.startsAt &&
                !values.startsAt.startOf("day").isBefore(minSelectableDate())
                  ? values.startsAt.startOf("day")
                  : minSelectableDate()
              }
              error={isPastDate(values.endsAt)}
              zIndex={5000}
              borderColor={swayyColors.border}
              borderRadius={swayyRadii.md}
            />
          </Grid2>
        </Grid2>
        {formError ? (
          <CustomTypography
            variant="body3"
            color={swayyColors.danger}
            sx={{ fontFamily: swayyFont, fontSize: 12, mt: 1.5 }}
          >
            {formError}
          </CustomTypography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton
          label="Cancel"
          onClick={handleClose}
          customStyles={{
            minHeight: 40,
            color: swayyColors.deepGreen,
            border: `1px solid ${swayyColors.border}`,
          }}
        />
        <AppButton
          label={
            store.saving
              ? isEdit
                ? "Saving..."
                : "Creating..."
              : isEdit
                ? "Save changes"
                : "Create coupon"
          }
          disabled={store.saving}
          onClick={() => void handleSubmit()}
          customStyles={{
            minHeight: 40,
            minWidth: 140,
            borderRadius: swayyRadii.md,
            bgcolor: swayyColors.pine,
            color: swayyColors.white,
            "&:hover": { bgcolor: swayyColors.pineHover },
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default observer(SwayyCreateCouponDialog);
